import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Newspaper, ArrowSquareOut } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Minimal news ticker that slides horizontally, pauses on hover,
 * and opens each item in a new tab. Source: affitti brevi RSS.
 */
export default function NewsTicker() {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    axios.get(`${API}/news/rss`).then(r => {
      setItems(r.data?.items || []);
    }).catch(() => { /* silent */ });
  }, []);

  if (!items.length) return null;

  // Double the items for seamless loop
  const loopItems = [...items, ...items];

  return (
    <div
      className="group relative w-full bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 border-y border-zinc-800 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="news-ticker"
    >
      <style dangerouslySetInnerHTML={{__html:`
        @keyframes ticker-slide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-track {
          animation: ticker-slide 240s linear infinite;
          will-change: transform;
        }
        .ticker-track.paused {
          animation-play-state: paused;
        }
        .ticker-item {
          position: relative;
        }
        .ticker-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          width: 3px;
          height: 3px;
          background: currentColor;
          opacity: .35;
          border-radius: 50%;
          transform: translateY(-50%);
        }
      `}} />

      {/* Fade edges */}
      <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-r from-zinc-950 to-transparent z-20" />
      <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 sm:w-24 bg-gradient-to-l from-zinc-950 to-transparent z-20" />

      <div className="relative flex items-center">
        {/* Fixed Label */}
        <div className="flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-rose-600 to-red-600 text-white z-30 relative">
          <Newspaper size={14} weight="bold" className="animate-pulse" />
          <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.15em] whitespace-nowrap">
            News affitti brevi
          </span>
        </div>

        {/* Scrolling content */}
        <div className={`ticker-track flex items-center whitespace-nowrap ${paused ? 'paused' : ''}`} style={{ width: 'max-content' }}>
          {loopItems.map((item, i) => (
            <a
              key={`${item.link}-${i}`}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="ticker-item inline-flex items-center gap-2.5 px-6 sm:px-8 py-2.5 text-zinc-300 hover:text-white transition-colors group/item"
              data-testid={`ticker-item-${i}`}
            >
              <span className="text-[12px] sm:text-[13px] font-semibold text-white max-w-[340px] sm:max-w-none truncate">
                {item.title}
              </span>
              {item.snippet && (
                <span className="text-[11px] sm:text-xs text-zinc-400 hidden sm:inline max-w-[280px] truncate">
                  — {item.snippet}
                </span>
              )}
              <ArrowSquareOut size={11} weight="bold" className="text-zinc-500 group-hover/item:text-rose-400 transition-colors opacity-0 group-hover/item:opacity-100" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
